import { useMoney } from '../hooks/useMoney';
/**
 * DELIVERY COST REPORT PAGE
 *
 * Analyze delivery costs with charts and cost-per-delivery metrics.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Banner,
  Spinner,
  Select,
  DataTable,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface CostBreakdown {
  cost_type: string;
  total: number;
  percentage: number;
}

interface DailyCost {
  date: string;
  total: number;
  deliveries: number;
  cost_per_delivery: number;
}

interface CostReport {
  total_costs: number;
  total_deliveries: number;
  cost_per_delivery: number;
  breakdown_by_type: CostBreakdown[];
  daily_costs: DailyCost[];
  period: {
    start: string;
    end: string;
  };
}

export default function DeliveryCostReport() {
  const navigate = useNavigate();
  const { fetchApi, loading, error } = useApi();
  const [report, setReport] = useState<CostReport | null>(null);
  const [period, setPeriod] = useState('month');

  const loadReport = useCallback(async () => {
    const data = await fetchApi<CostReport>(`/reports/delivery-costs?period=${period}`);
    if (data) {
      setReport(data);
    }
  }, [fetchApi, period]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const getCostTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      fuel: 'Fuel',
      salary: 'Salary',
      vehicle: 'Vehicle',
      tolls: 'Tolls',
      parking: 'Parking',
      maintenance: 'Maintenance',
      insurance: 'Insurance',
      other: 'Other',
    };
    return labels[type] || type;
  };

  const getCostTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      fuel: '#F59E0B',
      salary: '#3B82F6',
      vehicle: '#6B7280',
      tolls: '#8B5CF6',
      parking: '#EC4899',
      maintenance: '#10B981',
      insurance: '#14B8A6',
      other: '#9CA3AF',
    };
    return colors[type] || '#6B7280';
  };

  const formatCurrency = useMoney();

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
    });
  };

  if (loading && !report) {
    return (
      <Page title="Delivery Cost Report">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <Spinner size="large" />
                <Text as="p" tone="subdued">Loading report...</Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Delivery Cost Report"
      subtitle="Analyze delivery expenses and cost per delivery"
      backAction={{ content: 'Delivery Costs', onAction: () => navigate('/delivery-costs') }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Period Filter */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" blockAlign="end">
              <div style={{ width: '200px' }}>
                <Select
                  label="Period"
                  options={[
                    { label: 'This Week', value: 'week' },
                    { label: 'This Month', value: 'month' },
                    { label: 'Last 3 Months', value: '3months' },
                    { label: 'This Year', value: 'year' },
                  ]}
                  value={period}
                  onChange={setPeriod}
                />
              </div>
            </InlineStack>
          </Card>
        </Layout.Section>

        {report && (
          <>
            {/* Summary Cards */}
            <Layout.Section>
              <InlineStack gap="400" wrap>
                <Card>
                  <BlockStack gap="200">
                    <Text as="p" tone="subdued">Total Costs</Text>
                    <Text as="p" variant="headingXl" fontWeight="bold">
                      {formatCurrency(report.total_costs)}
                    </Text>
                  </BlockStack>
                </Card>
                <Card>
                  <BlockStack gap="200">
                    <Text as="p" tone="subdued">Total Deliveries</Text>
                    <Text as="p" variant="headingXl" fontWeight="bold">
                      {report.total_deliveries}
                    </Text>
                  </BlockStack>
                </Card>
                <Card>
                  <BlockStack gap="200">
                    <Text as="p" tone="subdued">Cost Per Delivery</Text>
                    <Text as="p" variant="headingXl" fontWeight="bold" tone={report.cost_per_delivery > 15 ? 'critical' : 'success'}>
                      {formatCurrency(report.cost_per_delivery)}
                    </Text>
                  </BlockStack>
                </Card>
              </InlineStack>
            </Layout.Section>

            {/* Cost Breakdown */}
            <Layout.Section variant="oneHalf">
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">Cost Breakdown by Type</Text>

                  {report.breakdown_by_type.length > 0 ? (
                    <BlockStack gap="300">
                      {report.breakdown_by_type.map((item) => (
                        <BlockStack gap="100" key={item.cost_type}>
                          <InlineStack align="space-between">
                            <Text as="span">{getCostTypeLabel(item.cost_type)}</Text>
                            <InlineStack gap="200">
                              <Text as="span" fontWeight="semibold">
                                {formatCurrency(item.total)}
                              </Text>
                              <Text as="span" tone="subdued">
                                ({item.percentage.toFixed(1)}%)
                              </Text>
                            </InlineStack>
                          </InlineStack>
                          <div style={{ position: 'relative', height: '8px', background: '#E5E7EB', borderRadius: '4px', overflow: 'hidden' }}>
                            <div
                              style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: `${item.percentage}%`,
                                background: getCostTypeColor(item.cost_type),
                                borderRadius: '4px',
                              }}
                            />
                          </div>
                        </BlockStack>
                      ))}
                    </BlockStack>
                  ) : (
                    <Text as="p" tone="subdued">No cost data for this period.</Text>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>

            {/* Daily Breakdown */}
            <Layout.Section variant="oneHalf">
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">Daily Summary</Text>

                  {report.daily_costs.length > 0 ? (
                    <DataTable
                      columnContentTypes={['text', 'numeric', 'numeric', 'numeric']}
                      headings={['Date', 'Costs', 'Deliveries', 'Per Delivery']}
                      rows={report.daily_costs.slice(0, 14).map((day) => [
                        formatDate(day.date),
                        formatCurrency(day.total),
                        day.deliveries,
                        formatCurrency(day.cost_per_delivery),
                      ])}
                    />
                  ) : (
                    <Text as="p" tone="subdued">No daily data for this period.</Text>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>

            {/* Insights */}
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">Insights</Text>

                  {report.cost_per_delivery > 15 ? (
                    <Banner tone="warning">
                      Your cost per delivery ({formatCurrency(report.cost_per_delivery)}) is above the
                      recommended target of €15. Consider optimizing routes or consolidating deliveries.
                    </Banner>
                  ) : (
                    <Banner tone="success">
                      Your cost per delivery ({formatCurrency(report.cost_per_delivery)}) is within
                      the healthy range. Keep up the good work!
                    </Banner>
                  )}

                  {report.breakdown_by_type.length > 0 && (
                    <BlockStack gap="200">
                      <Text as="p">
                        <strong>Largest expense:</strong>{' '}
                        {getCostTypeLabel(report.breakdown_by_type[0].cost_type)} at{' '}
                        {formatCurrency(report.breakdown_by_type[0].total)} ({report.breakdown_by_type[0].percentage.toFixed(1)}%)
                      </Text>
                    </BlockStack>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>
          </>
        )}
      </Layout>
    </Page>
  );
}
